import React from "react";
import { Field } from "react-final-form";
import { useIntl } from "react-intl";
import { string as yupString } from "yup";
import PropTypes from "prop-types";
import { PasswordInput } from "@ebsco-ui/ebsco-ui";
import CriterionIndicator from "./criterion-indicator";
import messages from "./password-messages";
import sharedMessages from "../../../../../shared-messages";

import "./password.scss";

/*
 * This object contains the keys to the messages for each criterion.
 *  These values must match the message keys in the messages file.
 */
const CRITERIA = {
  ONE_NUMBER: "oneNumber",
  SIX_CHARACTER_MINIMUM: "sixCharacterMinimum",
  ONE_SPECIAL_CHARACTER: "oneSpecialCharacter",
  OMIT_USERNAME: "omitUsername",
  OMIT_EMAIL: "omitEmail",
  BLACKLISTED_WORD: "blacklistedWord"
};

const blacklistedWords = require("./blacklisted-words.json");

const passwordValidationSchema = yupString()
  .min(6, CRITERIA.SIX_CHARACTER_MINIMUM)
  .matches(/\d/, CRITERIA.ONE_NUMBER)
  .matches(/[!@#$%^&*(),.?":{}|<>]/, CRITERIA.ONE_SPECIAL_CHARACTER);

const Password = props => {
  const { email, label, id, username, name } = props;

  const { formatMessage } = useIntl();
  /**
   * default value of empty string is necessary to allow validations
   * to display their errors when the provided value is undefined
   */
  const validateNewPassword = (value = "", formValues = "") => {
    try {
      const emailAddress =
        typeof email === "function" ? email(formValues) : email;
      const usernameValue =
        typeof username === "function" ? username(formValues) : username;
      passwordValidationSchema
        .test("omit-email", CRITERIA.OMIT_EMAIL, value => {
          if (emailAddress) {
            return (
              (value &&
                !value.toLowerCase().includes(emailAddress.toLowerCase())) ||
              false
            );
          }
          return value;
        })
        .test("omit-username", CRITERIA.OMIT_USERNAME, value => {
          if (usernameValue) {
            return (
              (value &&
                !value.toLowerCase().includes(usernameValue.toLowerCase())) ||
              false
            );
          }
          return value;
        })
        .test(
          "blacklisted-word",
          CRITERIA.BLACKLISTED_WORD,
          value =>
            value &&
            blacklistedWords.every(
              blacklistedWord =>
                !value.toLowerCase().includes(blacklistedWord.toLowerCase())
            )
        )
        .validateSync(value, {
          abortEarly: false
        });
    } catch ({ errors }) {
      return errors.reduce((accumulator, currentValue) => {
        accumulator[currentValue] = true;
        return accumulator;
      }, {});
    }
  };

  const errorToDisplay = meta => {
    return meta.error && meta.submitFailed
      ? formatMessage(messages.invalidNewPassword)
      : "";
  };

  const renderCriteria = error => {
    return Object.values(CRITERIA).map(criterion => {
      const isConditionMet = error && error[criterion] ? false : true;
      return (
        <CriterionIndicator
          key={criterion}
          className="password__criterion"
          label={formatMessage(messages[criterion])}
          isConditionMet={isConditionMet}
        />
      );
    });
  };

  const renderHelperText = ({ touched, error }) => {
    if (error) {
      const message = touched
        ? messages.unmetCriteria
        : messages.initialCriteria;
      return (
        <span className="screen-reader-text">{formatMessage(message)}</span>
      );
    } else {
      return null;
    }
  };

  return (
    <Field name={name} initialValue="" validate={validateNewPassword}>
      {({ input, meta }) => (
        <div>
          <PasswordInput
            id={id}
            error={errorToDisplay(meta)}
            label={label}
            showPasswordButtonText={formatMessage(sharedMessages.show)}
            hidePasswordButtonText={formatMessage(sharedMessages.hide)}
            descriptionId={"password-criteria"}
            aria-required="true"
            className="password__input"
            {...input}
          />
          <div id="password-criteria" className="password__criteria">
            {renderHelperText(meta)}
            {renderCriteria(meta.error)}
          </div>
        </div>
      )}
    </Field>
  );
};

Password.defaultProps = {
  name: "password"
};

Password.propTypes = {
  /** Either a string or a function.  If a function, the function will be provided all form values as the argument. */
  email: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  label: PropTypes.string.isRequired,
  username: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  id: PropTypes.string.isRequired,
  name: PropTypes.string
};

export { Password };
export default Password;
